import { Injectable } from '@angular/core';
import { CommonService } from '@shared/services/common.service';

@Injectable({
  providedIn: 'root'
})
export class IndexedDbService {
  private indexedDbData: { [key: string]: any } = {};
  private dbPromise: Promise<IDBDatabase> | null = null;
  private isSupported = false;
  private readonly DB_NAME = 'my-database';
  private readonly DB_VERSION = 1;
  private readonly STORE_NAME = 'dataStore';

  constructor(private cs: CommonService) {
    this.initializeDB();
  }

  private async initializeDB() {
    // Only initialize IndexedDB in browser environment
    if (!this.cs.isBrowser) {
      return;
    }

    try {
      // Check if IndexedDB is supported
      if (typeof window !== 'undefined' && 'indexedDB' in window) {
        this.isSupported = true;
        this.dbPromise = new Promise((resolve, reject) => {
          const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);
          
          request.onerror = () => reject(request.error);
          request.onsuccess = () => resolve(request.result);
          
          request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;
            if (!db.objectStoreNames.contains(this.STORE_NAME)) {
              db.createObjectStore(this.STORE_NAME);
            }
          };
        });
      } else {
        console.warn('IndexedDB is not supported in this browser');
      }
    } catch (error) {
      console.error('Failed to initialize IndexedDB:', error);
      this.isSupported = false;
    }
  }

  async setItem(key: string, value: any): Promise<void> {
    if (!this.isSupported || !this.dbPromise) {
      // Fallback to memory storage
      this.setIndexedDbData(key, value);
      return;
    }

    try {
      const db = await this.dbPromise;
      const transaction = db.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      
      return new Promise((resolve, reject) => {
        const request = store.put(value, key);
        request.onsuccess = () => {
          this.setIndexedDbData(key, value);
          resolve();
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Failed to set item in IndexedDB:', error);
      // Fallback to memory storage
      this.setIndexedDbData(key, value);
    }
  }

  set setIndexedDb(data: any) {
    this.indexedDbData = data;
  }

  getValueFromIndexedDb(key: string): any {
    return this.cs.isBrowser ? this.indexedDbData[key] : null;
  }

  setIndexedDbData(key: string, value: any): void {
    this.indexedDbData[key] = value;
  }

  async getAllItems<T = any>(): Promise<{ [key: string]: T }> {
    if (!this.isSupported || !this.dbPromise) {
      // Return memory storage data
      return this.indexedDbData as { [key: string]: T };
    }

    try {
      const db = await this.dbPromise;
      const transaction = db.transaction([this.STORE_NAME], 'readonly');
      const store = transaction.objectStore(this.STORE_NAME);

      return new Promise((resolve, reject) => {
        const result: { [key: string]: T } = {};
        const request = store.openCursor();
        
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            result[String(cursor.key)] = cursor.value;
            cursor.continue();
          } else {
            resolve(result);
          }
        };
        
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Failed to get all items from IndexedDB:', error);
      // Fallback to memory storage
      return this.indexedDbData as { [key: string]: T };
    }
  }

  async getItem<T = any>(key: string): Promise<T | undefined> {
    if (!this.isSupported || !this.dbPromise) {
      // Return from memory storage
      return this.indexedDbData[key] as T;
    }

    try {
      const db = await this.dbPromise;
      const transaction = db.transaction([this.STORE_NAME], 'readonly');
      const store = transaction.objectStore(this.STORE_NAME);
      
      return new Promise((resolve, reject) => {
        const request = store.get(key);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Failed to get item from IndexedDB:', error);
      // Fallback to memory storage
      return this.indexedDbData[key] as T;
    }
  }

  async removeItem(key: string): Promise<void> {
    delete this.indexedDbData[key];

    if (!this.isSupported || !this.dbPromise) {
      return;
    }

    try {
      const db = await this.dbPromise;
      const transaction = db.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      
      return new Promise((resolve, reject) => {
        const request = store.delete(key);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Failed to remove item from IndexedDB:', error);
    }
  }

  async clear(): Promise<void> {
    this.indexedDbData = {};

    if (!this.isSupported || !this.dbPromise) {
      return;
    }

    try {
      const db = await this.dbPromise;
      const transaction = db.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      
      return new Promise((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Failed to clear IndexedDB:', error);
    }
  }
}
